<template>
  <div v-if="hasItems">
    <FilterableChartPanel
      panel-prefix="portfolio"
      :raw-data="filteredStats.items"
      :x-axis-options="X_AXIS_OPTIONS"
      :stack-options="stackOptions"
      :breakdown-options="breakdownOptions"
      y-axis-field-name="count"
      :chart-height="400"
      :total-mwh="filteredStats.totalMwh"
      :row-count="filteredStats.count"
      :available-mwh="filteredStats.availableMwh"
      @filter-to="filterTableTo"
    />

    <div class="mt-4">
      <PortfolioItemsTable
        ref="portfolioTableRef"
        :selection="selection"
        :items="portfolioItemsStore.allItems"
        :enable-count-editing="true"
        :default-excluded-filter-values="DEFAULT_EXCLUDED_FILTER_VALUES"
        deselect-hidden-on-filter-change
        filter-url-param="portfolio_f"
        :filter-url-scope="filterUrlScope"
        show-partial-amount-hint
        @update:filtered-stats="filteredStats = $event"
      />
    </div>
  </div>
  <PlaceholderCardComponent
    v-else
    :title="t('dashboard.portfolio.no_portfolio_data_title')"
    :subtitle="t('dashboard.portfolio.no_portfolio_data_subtitle')"
  />
</template>

<script setup lang="ts">
import PlaceholderCardComponent from "@/components/common/PlaceholderCardComponent.vue";
import { createStackOption } from "@/composables/filterableChartUtils";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import type { SelectableFilterColumn } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import type {
  BreakdownColumn,
  BreakdownOption,
} from "@/dashboard/inventory_management/overview/breakdownSegments";
import FilterableChartPanel from "@/dashboard/inventory_management/overview/FilterableChartPanel.vue";
import type { FilteredPortfolioStats } from "@/types";
import { StackOption, XAxisOption } from "@/types/ChartData";
import { getRegistryLabel } from "@/utils/registries";
import { computed, onUnmounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

// Overview lists every status, but expired and non-transferrable GOs start hidden: a Default chip
// the user can remove. Only values present in the company's data are hidden.
const DEFAULT_EXCLUDED_FILTER_VALUES: Partial<
  Record<SelectableFilterColumn, string[]>
> = { status: ["EXPIRED", "NON_TRANSFERRABLE"] };

// Order is part of the URL: `portfolio_x` stores an index into this list.
const X_AXIS_OPTIONS: XAxisOption[] = [
  {
    fieldName: "production_month",
    label: "Production Month",
    labelKey: "charts.fields.production_month",
    axisType: "date",
  },
  {
    fieldName: "production_device_name",
    label: "Production Device",
    labelKey: "charts.fields.production_device_name",
    axisType: "string",
    // Production device name is not translated
  },
  {
    fieldName: "portfolio_type",
    label: "Portfolio Type",
    labelKey: "charts.fields.portfolio_type",
    axisType: "string",
    valueTranslationKeyPrefix: "charts.values.portfolio_type",
  },
  {
    fieldName: "status",
    label: "Status",
    labelKey: "charts.fields.status",
    axisType: "string",
    valueTranslationKeyPrefix: "charts.values.certificate_status",
  },
  {
    fieldName: "technology_type",
    label: "Technology Type",
    labelKey: "charts.fields.technology_type",
    axisType: "string",
    valueTranslationKeyPrefix: "common.technology",
  },
  {
    fieldName: "production_support",
    label: "Production Support",
    labelKey: "charts.fields.production_support",
    axisType: "string",
    valueTranslationKeyPrefix: "charts.values.production_support",
  },
  {
    fieldName: "company_name",
    label: "Company Name",
    labelKey: "charts.fields.company_name",
    axisType: "string",
    // Company name is not translated
  },
];

const { t } = useI18n();
const portfolioItemsStore = usePortfolioItemsStore();
const selection = usePortfolioSelection({ syncToUrl: true });
const portfolioTableRef = useTemplateRef("portfolioTableRef");

// The table owns the only filter set; the chart and the summary visualise what it leaves.
const filteredStats = ref<FilteredPortfolioStats>({
  count: 0,
  totalMwh: 0,
  availableMwh: 0,
  items: [],
});

const hasItems = computed(() => portfolioItemsStore.allItems.length > 0);

// Stamps the URL-persisted filters with the companies they were made for — one id for a single
// company, the sorted selected set in the consolidated view — so a company swap never applies
// another company's filters.
const filterUrlScope = computed(() =>
  [
    ...new Set(
      portfolioItemsStore.allItems
        .map((item) => item.company_id)
        .filter((id): id is number => id != null)
    ),
  ]
    .sort((a, b) => a - b)
    .join(",")
);

// A breakdown segment filters the table to its value; clicking it again once that is the only
// value clears the filter. The table's IN filter compares raw values, so null (rows without a
// technology) matches exactly those rows.
const filterTableTo = (column: BreakdownColumn, value: string | null) => {
  const table = portfolioTableRef.value;
  if (!table) return;
  const current = table.filterValues[column];
  const onlyThisValue = current.length === 1 && current[0] === value;
  table.setColumnFilter(column, onlyThisValue ? [] : [value]);
};

// Colours come from every item, not just the filtered ones, so a value keeps its colour as
// filters change.
const statusStack = computed(() =>
  createStackOption(portfolioItemsStore.allItems, "status", {
    valueTranslationKeyPrefix: "charts.values.certificate_status",
  })
);
const technologyStack = computed(() =>
  createStackOption(portfolioItemsStore.allItems, "technology_type", {
    valueTranslationKeyPrefix: "common.technology",
  })
);
const portfolioTypeStack = computed(() =>
  createStackOption(portfolioItemsStore.allItems, "portfolio_type", {
    valueTranslationKeyPrefix: "charts.values.portfolio_type",
  })
);

const stackOptions = computed<StackOption[]>(() => [
  statusStack.value,
  // Production device name is not translated
  createStackOption(portfolioItemsStore.allItems, "production_device_name"),
  technologyStack.value,
  portfolioTypeStack.value,
  createStackOption(portfolioItemsStore.allItems, "registry_region", {
    valueLabelFn: getRegistryLabel,
  }),
  // Company name is not translated
  createStackOption(portfolioItemsStore.allItems, "company_name"),
  createStackOption(portfolioItemsStore.allItems, "production_support", {
    valueTranslationKeyPrefix: "charts.values.production_support",
  }),
]);

const breakdownOptions = computed<BreakdownOption[]>(() => [
  technologyStack.value,
  statusStack.value,
  portfolioTypeStack.value,
]);

onUnmounted(() => {
  selection.clearSelection();
});
</script>
